/**
 * Cookie Settings Modal for Baakas
 * Detailed cookie preferences with category toggles
 */

import React, { useState, useEffect, useRef } from 'react';
import { X, Cookie, Shield, BarChart3, Megaphone, Zap, Check, Info } from 'lucide-react';
import { gsap } from 'gsap';
import { useCookieConsent, ConsentPreferences } from '../contexts/CookieConsentContext';
import { Link } from 'react-router-dom';

interface CookieSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CookieCategory {
  id: keyof Omit<ConsentPreferences, 'timestamp'>;
  name: string;
  description: string;
  icon: React.ReactNode;
  required: boolean;
  cookies: { name: string; purpose: string; expiry: string }[];
}

const cookieCategories: CookieCategory[] = [
  {
    id: 'essential',
    name: 'Essential',
    description: 'Required for the website to function. These cannot be disabled.',
    icon: <Shield className="w-5 h-5 text-green-600" />,
    required: true,
    cookies: [
      { name: 'Authentication', purpose: 'Keeps you logged in', expiry: 'Session' },
      { name: 'Cart Data', purpose: 'Remembers your shopping cart', expiry: '30 days' },
      { name: 'Cookie Consent', purpose: 'Stores your cookie preferences', expiry: '1 year' },
      { name: 'Session ID', purpose: 'Identifies your browsing session', expiry: '1 day' },
    ],
  },
  {
    id: 'functional',
    name: 'Functional',
    description: 'Enhance your experience by remembering preferences and enabling features.',
    icon: <Zap className="w-5 h-5 text-blue-600" />,
    required: false,
    cookies: [
      { name: 'Theme Preference', purpose: 'Remembers dark/light mode', expiry: '1 year' },
      { name: 'Recently Viewed', purpose: 'Shows products you browsed', expiry: '30 days' },
      { name: 'Search History', purpose: 'Quick access to recent searches', expiry: '30 days' },
      { name: 'Category Cache', purpose: 'Faster page loads', expiry: '30 minutes' },
      { name: 'Product Cache', purpose: 'Reduces loading times', expiry: '30 minutes' },
    ],
  },
  {
    id: 'analytics',
    name: 'Analytics',
    description: 'Help us understand how visitors use our site to improve it.',
    icon: <BarChart3 className="w-5 h-5 text-purple-600" />,
    required: false,
    cookies: [
      { name: 'User Behavior', purpose: 'Tracks which sections you visit', expiry: '30 days' },
      { name: 'Product Interactions', purpose: 'Records views and clicks', expiry: 'Session' },
      { name: 'Analytics Session', purpose: 'Aggregate usage statistics', expiry: '30 days' },
    ],
  },
  {
    id: 'marketing',
    name: 'Marketing',
    description: 'Used to show relevant promotions and personalized offers.',
    icon: <Megaphone className="w-5 h-5 text-orange-600" />,
    required: false,
    cookies: [
      { name: 'Ad Preferences', purpose: 'Controls ad frequency', expiry: '90 days' },
      { name: 'Promo Tracking', purpose: 'Tracks promotion engagement', expiry: '90 days' },
      { name: 'Popup Dismissed', purpose: 'Remembers closed promotions', expiry: '30 days' },
    ],
  },
];

const CookieSettingsModal: React.FC<CookieSettingsModalProps> = ({ isOpen, onClose }) => {
  const { preferences, updatePreferences, acceptAll, acceptEssentialOnly } = useCookieConsent();
  const backdropRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  
  // Local state for toggles before saving
  const [localPrefs, setLocalPrefs] = useState({
    functional: preferences.functional,
    analytics: preferences.analytics,
    marketing: preferences.marketing,
  });

  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Sync local state with context when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalPrefs({
        functional: preferences.functional,
        analytics: preferences.analytics,
        marketing: preferences.marketing,
      });
    }
  }, [isOpen, preferences]);

  // Animate modal
  useEffect(() => {
    if (isOpen) {
      if (backdropRef.current) {
        gsap.fromTo(backdropRef.current, { opacity: 0 }, { opacity: 1, duration: 0.2 });
      }
      if (modalRef.current) {
        gsap.fromTo(
          modalRef.current,
          { scale: 0.95, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.3, ease: "back.out(1.7)" }
        );
      }
    } else {
      if (backdropRef.current) {
        gsap.to(backdropRef.current, { opacity: 0, duration: 0.2 });
      }
      if (modalRef.current) {
        gsap.to(modalRef.current, { scale: 0.95, opacity: 0, duration: 0.3, ease: "power2.in" });
      }
    }
  }, [isOpen]);

  const handleToggle = (category: 'functional' | 'analytics' | 'marketing') => {
    setLocalPrefs(prev => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSave = () => {
    updatePreferences(localPrefs);
    onClose();
  };

  const handleAcceptAll = () => {
    acceptAll();
    onClose();
  };

  const handleEssentialOnly = () => {
    acceptEssentialOnly();
    onClose();
  };

  const toggleExpand = (categoryId: string) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  if (!isOpen) return null;

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/50"
      onClick={onClose}
    >
      <div
        ref={modalRef}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
            {/* Header */}
            <div className="bg-gradient-to-r from-[#da2516] to-[#ff4433] px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3 text-white">
                <Cookie className="w-6 h-6" />
                <div>
                  <h2 className="font-bold text-lg">Cookie Settings</h2>
                  <p className="text-white/80 text-sm">Manage your privacy preferences</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white p-2 rounded-full hover:bg-white/10 transition-colors"
                aria-label="Close settings"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
              <div className="p-6 space-y-4">
                {/* Info box */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Your privacy matters to us</p>
                    <p className="text-blue-700">
                      We only use cookies to improve your experience. We don't sell your data to third parties.{' '}
                      <Link to="/legal/cookies" className="underline" onClick={onClose}>
                        Read our Cookie Policy
                      </Link>
                    </p>
                  </div>
                </div>

                {/* Categories */}
                {cookieCategories.map((category) => {
                  const isEnabled = category.required || localPrefs[category.id as keyof typeof localPrefs];
                  const isExpanded = expandedCategory === category.id;

                  return (
                    <CookieCategoryItem
                      key={category.id}
                      category={category}
                      isEnabled={isEnabled}
                      isExpanded={isExpanded}
                      onToggle={() => !category.required && handleToggle(category.id as 'functional' | 'analytics' | 'marketing')}
                      onExpand={() => toggleExpand(category.id)}
                      disabled={category.required}
                    />
                  );
                })}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={handleEssentialOnly}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium text-sm hover:bg-gray-100 rounded-lg transition-colors"
              >
                Essential Only
              </button>
              <div className="flex gap-3">
                <button
                  onClick={handleAcceptAll}
                  className="px-4 py-2 border border-gray-300 hover:border-gray-400 text-gray-700 font-medium rounded-lg transition-colors text-sm"
                >
                  Accept All
                </button>
                <button
                  onClick={handleSave}
                  className="px-6 py-2 bg-[#da2516] hover:bg-[#b91f12] text-white font-medium rounded-lg transition-colors text-sm shadow-sm"
                >
                  Save Preferences
                </button>
              </div>
            </div>
          </div>
    </div>
  );
};

// Separate component for cookie category item to use hooks properly
const CookieCategoryItem: React.FC<{
  category: CookieCategory;
  isEnabled: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onExpand: () => void;
  disabled: boolean;
}> = ({ category, isEnabled, isExpanded, onToggle, onExpand, disabled }) => {
  const detailsRef = useRef<HTMLDivElement>(null);

  // Animate details expansion
  useEffect(() => {
    if (detailsRef.current) {
      if (isExpanded) {
        gsap.fromTo(
          detailsRef.current,
          { height: 0, opacity: 0 },
          { height: 'auto', opacity: 1, duration: 0.2, ease: "power2.out" }
        );
      } else {
        gsap.to(detailsRef.current, {
          height: 0,
          opacity: 0,
          duration: 0.2,
          ease: "power2.in"
        });
      }
    }
  }, [isExpanded]);

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Category Header */}
      <div className="p-4 bg-gray-50 flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <div className="p-2 bg-white rounded-lg shadow-sm">
            {category.icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{category.name}</h3>
              {category.required && (
                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">
                  Always on
                </span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-0.5">
              {category.description}
            </p>
          </div>
        </div>

        {/* Toggle */}
        <button
          onClick={onToggle}
          disabled={disabled}
          className={`relative w-12 h-6 rounded-full transition-colors ${
            isEnabled
              ? 'bg-[#da2516]'
              : 'bg-gray-300'
          } ${disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform flex items-center justify-center ${
              isEnabled ? 'translate-x-6' : 'translate-x-0'
            }`}
          >
            {isEnabled && <Check className="w-3 h-3 text-[#da2516]" />}
          </span>
        </button>
      </div>

      {/* Expand button */}
      <button
        onClick={onExpand}
        className="w-full px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 flex items-center justify-center gap-1 border-t border-gray-100"
      >
        {isExpanded ? 'Hide details' : 'Show cookies used'}
        <svg
          className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Cookie details */}
      <div ref={detailsRef} className="overflow-hidden" style={{ height: 0, opacity: 0 }}>
        <div className="px-4 pb-4 pt-2 space-y-2 bg-white border-t border-gray-100">
          {category.cookies.map((cookie, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between text-sm py-2 border-b border-gray-50 last:border-0"
            >
              <div>
                <span className="font-medium text-gray-800">{cookie.name}</span>
                <p className="text-gray-500 text-xs">{cookie.purpose}</p>
              </div>
              <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                {cookie.expiry}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CookieSettingsModal;
